/**
 * MandateMobileShell — phone frame for the Mandate app.
 *
 * Same rollback-safe pattern as MobileShell / PlanningMobileShell:
 *   - Injected by a single conditional in MandateLayout.tsx
 *   - Isolated entirely under src/mobile/**
 *   - Kill-switch: useMobileExperience() in MandateLayout
 *
 * One page per address (roadmap M-7, 2026-10-30): the dashboard's phone copy
 * (MandateMobileHome) was removed; every mandate page, the dashboard included,
 * is the main page inside this frame.
 *
 * Embedded platform pages (items, sessions, tools) also fall through to their
 * desktop components, which operate on the ProductSurfaceProvider mandate context.
 */
import { type ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './mobile.css';
import { ToastProvider } from '../components/ui/Toast';
import { NotificationToast } from '../components/NotificationToast';
import { CommandPalette } from '../components/prioritization/CommandPalette';
import { Loader2 } from 'lucide-react';
import { MandateMobileHeader } from './MandateMobileHeader';
import { MobileTabBar } from './MobileTabBar';
import { MobileDrawer } from './MobileDrawer';
import {
  MANDATE_TAB_ITEMS,
  MANDATE_DRAWER_SECTIONS,
  activeMandateTabHref,
  isMandateTopLevelPath,
} from './mandateNavConfig';

interface MandateMobileShellProps {
  children: ReactNode;
  /** When true, platform sub-paths (items, sessions, tools, lineage) are ready to render. */
  platformReady?: boolean;
}

/** Platform sub-paths that require the mandate space to be ready before rendering. */
const PLATFORM_PATH_SEGMENTS = ['/items', '/sessions', '/reports', '/platform', '/lineage', '/tools'];

/** Participate and live session routes own their own fullscreen dark design — skip mobile chrome. */
const SESSION_PARTICIPATE_RE = /^\/apps\/mandate\/sessions\/[^/]+\/participate/;
const SESSION_LIVE_RE = /^\/apps\/mandate\/sessions\/[^/]+\/live/;

export function MandateMobileShell({ children, platformReady = true }: MandateMobileShellProps) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isPlatformPath = PLATFORM_PATH_SEGMENTS.some((seg) => location.pathname.includes(seg));
  const isParticipate = SESSION_PARTICIPATE_RE.test(location.pathname);
  const isLive = SESSION_LIVE_RE.test(location.pathname);

  const activeHref = drawerOpen ? null : activeMandateTabHref(location.pathname);
  const showHeader = !isMandateTopLevelPath(location.pathname);
  const mainPaddingTop = showHeader
    ? 'calc(var(--pa-header-h) + var(--pa-safe-top))'
    : 'var(--pa-safe-top)';

  if (isParticipate || isLive) {
    return <ToastProvider>{children}</ToastProvider>;
  }

  return (
    <ToastProvider>
      <div className="pa-mobile min-h-screen bg-white dark:bg-[var(--bg)] text-gray-900 dark:text-[var(--text-pri)]">
        <NotificationToast />
        <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

        <MandateMobileHeader visible={showHeader} />

        <main
          className="pa-scroll"
          style={{
            paddingTop: mainPaddingTop,
            paddingBottom: 'calc(var(--pa-tabbar-h) + var(--pa-safe-bottom))',
            minHeight: '100vh',
          }}
        >
          {isPlatformPath && !platformReady ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            // The phone's version of the desktop frame's px-6 page gutter.
            <div className="px-4 pt-3 pb-4 min-w-0">{children}</div>
          )}
        </main>

        <MobileTabBar
          items={MANDATE_TAB_ITEMS}
          activeHref={activeHref}
          onMore={() => setDrawerOpen(true)}
          moreOpen={drawerOpen}
        />

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sections={MANDATE_DRAWER_SECTIONS}
          switchers="space"
        />
      </div>
    </ToastProvider>
  );
}
